import { hasValue } from '@go-widgets/well-logs-widget/dist/utils';
import { Responsive as ReactGridLayout } from '@profgeosoft/go-react-grid-layout';
import type { ItemCallback, Layout, Layouts } from '@profgeosoft/go-react-grid-layout';
import { ReduceLight as CloseFullscreenIcon } from '@repo/go-icons';
import { Button, Scrollbar } from '@repo/go-ui';
import clsx from 'clsx';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { RefObject, UIEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { ScrollState } from 'react-scrollbars-custom/dist/types/types';

import type { TWellListView } from 'src/api-types/wells.types';
import type { TabEntity } from 'src/entities/tab/TabEntity';
import { useService } from 'src/packages/di';

import { getCurrentBreakpoint } from '../../utils/getCurrentBreakpoint';
import { DirectionalDrillingWidgetEntity } from '../directional-drilling-widget/DirectionalDrillingWidget.entity';
import { DrillingMultiAnalysisWidgetEntity } from '../drilling-multi-analysis-widget/DrillingMultiAnalysisWidget.entity';
import { HeatmapWidgetEntity } from '../heatmap-widget/HeatmapWidget.entity';
import { ImageBroadcastWidgetEntity } from '../image-broadcast-widget/ImageBroadcastWidget.entity';
import { OperationalParametersWidgetEntity } from '../operational-parameters-widget/OperationalParametersWidget.entity';
import { RemoveObjectModal } from '../remove-object-modal/RemoveObjectModal';
import { VideobroadcastWidgetEntity } from '../videobroadcast-widget/VideobroadcastWidget.entity';
import { WellAnalyseWidgetEntity } from '../well-analyse-widget/WellAnalyseWidget.entity';
import { DayDepthChartWidgetEntity } from '../well-day-depth-widget/DayDepthChartWidget.entity';
import { WellLogsManagerWidgetEntity } from '../well-logs-manager-widget/WellLogsManagerWidget.entity';
import { WellLogsWidgetEntity } from '../well-logs-widget/WellLogsWidget.entity';

import {
  breakpoints,
  cols,
  X_CONTAINER_PADDING,
  X_WIDGETS_MARGIN,
  Y_CONTAINER_PADDING,
  Y_WIDGETS_MARGIN,
} from './consts';
import styles from './Workbench.module.scss';
import { WorkbenchStore } from './Workbench.store';

import './widget-resize-handle/WidgetResizeHandle.scss';

const PADDINGS: [number, number] = [X_CONTAINER_PADDING, Y_CONTAINER_PADDING];
const MARGINS: [number, number] = [X_WIDGETS_MARGIN, Y_WIDGETS_MARGIN];

type Props = {
  tab: TabEntity;
  isFullscreenMode: boolean;
  workspaceRef: RefObject<HTMLDivElement>;
  onExitFullscreen: VoidFunction;
  wellListView?: TWellListView;
};

export const Workbench = observer(function Workbench({ tab, isFullscreenMode, workspaceRef, onExitFullscreen }: Props) {
  const { t } = useTranslation();

  const ref = useRef<HTMLDivElement>(null);

  const [draggingWidgetId, setDraggingWidgetId] = useState<string | null>(null);
  const [resizingWidgetId, setResizingWidgetId] = useState<string | null>(null);
  const [gridRenderKey, setGridRenderKey] = useState(0);

  const {
    layouts,
    widgets,
    width,
    offsetTop,
    workbenchViewportHeight,
    currentBreakpoint,
    onLayoutChange,
    getExistingWidgetLayout,
    createWellsFilterWidget,
    createWellLogsWidget,
    createWellAnalyseWidget,
    createImageBroadcastWidget,
    createOperationalParametersWidget,
    createDirectionalDrillingWidget,
    createVideobroadcastWidget,
    createHeatmapWidget,
    createDrillingMultiAnalysisWidget,
    setOffsetTop,
    setHeight,
    setRealHeight,
    setWidth,
    fullscreenWidget,
    groupSelectStore,
    wellListCombinedData,
  } = tab;

  const [store] = useState(() => new WorkbenchStore(tab));

  const {
    widgetsDisplayingPriorityManager,
    effect,
    deleteWidget,
    onDeleteWidgetAccept,
    onDeleteWidgetCancel,
    deleteWidgetController,
    rowHeight,
    minWorkbenchDimensions,
  } = store;

  const widgetRendererService = useService('widgetRenderer');

  const _onLayoutChange = (_: Layout[], allLayouts: Layouts) => {
    onLayoutChange(allLayouts);
  };

  const handleWidgetChangingPositionStart: ItemCallback = (_, item) => {
    setDraggingWidgetId(item.i);
  };

  const handleWidgetChangingPositionEnd: ItemCallback = (layout, old, newL) => {
    setDraggingWidgetId(null);
  };

  const handleWidgetResizeStart: ItemCallback = (_, item) => {
    setResizingWidgetId(item.i);
  };

  const handleWidgetResizeStop: ItemCallback = () => {
    setResizingWidgetId(null);
  };

  useEffect(() => {
    const resizing = !!resizingWidgetId;
    const moving = !!draggingWidgetId;

    widgets.forEach((widget) => {
      if (
        widget instanceof DayDepthChartWidgetEntity ||
        widget instanceof WellLogsWidgetEntity ||
        widget instanceof DrillingMultiAnalysisWidgetEntity ||
        widget instanceof DirectionalDrillingWidgetEntity ||
        widget instanceof OperationalParametersWidgetEntity ||
        widget instanceof HeatmapWidgetEntity ||
        widget instanceof ImageBroadcastWidgetEntity ||
        widget instanceof WellAnalyseWidgetEntity ||
        widget instanceof VideobroadcastWidgetEntity ||
        widget instanceof WellLogsManagerWidgetEntity
      ) {
        const inFocus = store.hoveredWidgetId === widget.uuid;

        widget.state?.setAppParams({
          resizing,
          moving,
          inFocus,
        });
      }
    });
  }, [draggingWidgetId, resizingWidgetId, store.hoveredWidgetId, widgets]);

  // Это костыль, чтобы спастись от залипания курсора на крае виджета при перетаскивании
  // Надо его будет вынести в React-grid-layout в задаче
  const forceStopWidgetInteraction = useCallback(() => {
    if (!draggingWidgetId && !resizingWidgetId) {
      return;
    }

    setDraggingWidgetId(null);
    setResizingWidgetId(null);
    document.body.style.cursor = '';
    document.dispatchEvent(new MouseEvent('mouseup', { bubbles: true }));
    if ('PointerEvent' in window) {
      document.dispatchEvent(new PointerEvent('pointerup', { bubbles: true }));
    }
    setGridRenderKey((prev) => prev + 1);
  }, [draggingWidgetId, resizingWidgetId]);

  const onScrollUpdate = (scrollState: UIEvent<HTMLDivElement, globalThis.UIEvent> | ScrollState) => {
    if ('contentScrollHeight' in scrollState) {
      setRealHeight(scrollState.contentScrollHeight);
    }

    if ('scrollTop' in scrollState) {
      setOffsetTop(scrollState.scrollTop);
    }
  };

  const shouldRenderGridLayout = !!width && !!tab.workbenchViewportHeight && !!rowHeight;

  useEffect(effect, [effect]);

  useEffect(() => {
    const onWindowBlur = () => {
      forceStopWidgetInteraction();
    };

    const onVisibilityChange = () => {
      if (document.hidden) {
        forceStopWidgetInteraction();
      }
    };

    window.addEventListener('blur', onWindowBlur);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      window.removeEventListener('blur', onWindowBlur);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [forceStopWidgetInteraction]);

  useEffect(() => {
    const container = ref.current;

    if (container) {
      const { width } = container.getBoundingClientRect();
      setWidth(width);
      let RAFId: number | null = null;

      const resizeObserver = new ResizeObserver((entries) => {
        const containerEntry = entries.find((e) => e.target === container);

        RAFId = window.requestAnimationFrame((): void => {
          if (!containerEntry) {
            return;
          }

          const { height, width } = containerEntry.contentRect;
          setHeight(Math.trunc(height) - Y_CONTAINER_PADDING * 2);
          setWidth(Math.trunc(width));
        });
      });

      resizeObserver.observe(container);

      return () => {
        if (hasValue(RAFId)) {
          window.cancelAnimationFrame(RAFId);
        }

        resizeObserver.disconnect();
      };
    }
  }, [setHeight, setWidth, ref]);

  const minWidth = useMemo(() => {
    if (!hasValue(minWorkbenchDimensions.width)) {
      return null;
    }

    return minWorkbenchDimensions.width + X_CONTAINER_PADDING * 2;
  }, [minWorkbenchDimensions.width]);

  const minHeight = useMemo(() => {
    if (!hasValue(minWorkbenchDimensions.height)) {
      return null;
    }

    return minWorkbenchDimensions.height + Y_CONTAINER_PADDING * 2;
  }, [minWorkbenchDimensions.height]);

  return (
    <div ref={workspaceRef} className={clsx(styles.contentWrapper, isFullscreenMode && styles.contentWrapperFSMode)}>
      <Scrollbar
        className={styles.workbenchScrollers}
        onUpdate={onScrollUpdate}
        noScrollX={!!draggingWidgetId}
        noScrollY={widgets.some((w) => w.isFullscreen)}
      >
        <div
          ref={ref}
          className={styles.gridOverflow}
          style={{
            minWidth: minWidth ?? undefined,
            minHeight: minHeight ?? undefined,
          }}
        >
          {shouldRenderGridLayout && (
            <ReactGridLayout
              key={gridRenderKey}
              width={Math.trunc(width)}
              // возможно, стоит перенести скролл в библиотеку, чтобы не прокидывать сомнительные пропы
              containerViewportHeight={Math.trunc(workbenchViewportHeight)}
              offsetTop={offsetTop}
              //
              cols={cols}
              saveLayoutItemsPositionBetweenBreakpoints={true}
              autoSize={false}
              maxRows={500}
              breakpoints={breakpoints}
              breakpoint={currentBreakpoint ?? 'md'}
              preventCollision={false}
              className={styles.content}
              rowHeight={rowHeight}
              compressLayout={true}
              magnetizingDistance={15}
              compactType="horizontal"
              draggableHandle=".drag-handle"
              layouts={layouts}
              onResizeStart={handleWidgetResizeStart}
              onResizeStop={handleWidgetResizeStop}
              onLayoutChange={_onLayoutChange}
              onDrag={handleWidgetChangingPositionStart}
              onDragStop={handleWidgetChangingPositionEnd}
              containerPadding={PADDINGS}
              margin={MARGINS}
              allowOverlap={true}
            >
              {widgets.map((widget) => {
                let isFullscreen = false;
                const existingLayout = getExistingWidgetLayout(widget.uuid, getCurrentBreakpoint(width));

                if (
                  widget instanceof WellLogsWidgetEntity ||
                  // widget instanceof WellDetailsWidgetEntity ||
                  widget instanceof WellAnalyseWidgetEntity ||
                  widget instanceof ImageBroadcastWidgetEntity ||
                  widget instanceof DirectionalDrillingWidgetEntity ||
                  widget instanceof VideobroadcastWidgetEntity ||
                  widget instanceof DayDepthChartWidgetEntity ||
                  widget instanceof WellLogsManagerWidgetEntity ||
                  widget instanceof HeatmapWidgetEntity ||
                  widget instanceof DrillingMultiAnalysisWidgetEntity
                ) {
                  isFullscreen = widget.isFullscreen;
                }

                const handleClickOnWidget = () => widgetsDisplayingPriorityManager.placeWidgetOnTop(widget);
                const handleMouseOver = () => {
                  if (widget.uuid !== store.hoveredWidgetId) {
                    store.setHoveredWidgetId(widget.uuid);
                  }
                };

                const renderWidget = widgetRendererService.renderWidget(widget, {
                  groupSelectStore,
                  onWidgetDelete: deleteWidget,
                  widgetsDisplayingPriorityManager,
                  wellListEntity: wellListCombinedData,
                });

                // Нужно, чтобы список скважин был поверх виджета в режиме fullscreen
                const widgetZIndex = (() => {
                  if (draggingWidgetId === widget.uuid || resizingWidgetId === widget.uuid) {
                    return 999;
                  }

                  if (isFullscreen) {
                    return 998;
                  }

                  return widget.zIndex;
                })();

                return (
                  <article
                    key={widget.uuid}
                    data-grid={
                      existingLayout || {
                        i: widget.uuid,
                        x: widget.position.x,
                        y: widget.position.y,
                        w: widget.size.w,
                        h: widget.size.h,
                        minW: widget.sizeBoundaries.min?.w,
                        minH: widget.sizeBoundaries.min?.h,
                        maxW: widget.sizeBoundaries.max?.w,
                        maxH: widget.sizeBoundaries.max?.h,
                        minPxW: widget.sizeBoundaries.minInPixels.w,
                        minPxH: widget.sizeBoundaries.minInPixels.h,
                        maxPxW: widget.sizeBoundaries.maxInPixels?.w,
                        maxPxH: widget.sizeBoundaries.maxInPixels?.h,
                        isQuickResizeAllowed: true,
                        resizeHandles: ['e', 's', 'w', 'n', 'se', 'sw', 'nw', 'ne'],
                      }
                    }
                    className={clsx(styles.itemWrapper, isFullscreen && styles.itemWrapperFullscreen)}
                    style={{
                      zIndex: widgetZIndex,
                    }}
                    onClick={handleClickOnWidget}
                    onMouseMove={handleMouseOver}
                    onMouseLeave={() => store.setHoveredWidgetId(null)}
                  >
                    <div
                      className={clsx(
                        styles.item,
                        isFullscreen && styles.itemFullscreen,
                        isFullscreen && isFullscreenMode && styles.itemFullscreenWithoutHeader,
                        !isFullscreen && !!fullscreenWidget && styles.itemHidden,
                      )}
                    >
                      {renderWidget({
                        onCreateWellsFilterWidget: createWellsFilterWidget,
                        onCreateWellLogsWidget: createWellLogsWidget,
                        onCreateWellAnalyseWidget: createWellAnalyseWidget,
                        onCreateImageBroadcastWidget: createImageBroadcastWidget,
                        onCreateOperationalParametersWidget: createOperationalParametersWidget,
                        onCreateDirectionalDrillingWidget: createDirectionalDrillingWidget,
                        onCreateVideobroadcastWidget: createVideobroadcastWidget,
                        onCreateHeatmapWidget: createHeatmapWidget,
                        onCreateDrillingMultiAnalysisWidget: createDrillingMultiAnalysisWidget,
                      })}
                    </div>
                  </article>
                );
              })}
            </ReactGridLayout>
          )}
        </div>
      </Scrollbar>

      {isFullscreenMode && (
        <Button
          title={t('dashboard:Toolbar.exitFullScreen')}
          variant="flat"
          className={styles.exitFSButton}
          icon={<CloseFullscreenIcon width={32} height={32} />}
          onClick={onExitFullscreen}
        />
      )}

      <RemoveObjectModal
        title={t('modals:RemoveWidgetModal.title')}
        desc={t('modals:RemoveWidgetModal.desc')}
        isOpened={!!deleteWidgetController}
        onConfirm={onDeleteWidgetAccept}
        onCancel={onDeleteWidgetCancel}
      />
    </div>
  );
});
